import { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { format, startOfMonth, endOfMonth, addMonths, subMonths, addDays, startOfDay } from 'date-fns';
import { da } from 'date-fns/locale';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import KPICard from '../../components/common/KPICard';
import UtilizationCalendar from '../../components/charts/UtilizationCalendar';
import CargoCalendar from '../../components/charts/CargoCalendar';
import { useCalendarUtilization, useTrailers, useCargoCalendar } from '../../hooks/useApi';
import { api } from '../../services/api';

// Type for cargo colors API response
interface CargoColorsResponse {
  colors: Record<string, string>;
  customColors: Record<string, string>;
  defaultColors: Record<string, string>;
}

export default function TrailerCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tabValue, setTabValue] = useState(0); // 0 = Cargo Calendar, 1 = Utilization Calendar
  const [periodDays, setPeriodDays] = useState(7);
  const [cargoStartDate, setCargoStartDate] = useState(startOfDay(new Date()));

  // Dates for utilization calendar (monthly)
  const monthFrom = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
  const monthTo = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

  // Dates for cargo calendar (period based)
  const cargoFrom = format(cargoStartDate, 'yyyy-MM-dd');
  const cargoTo = format(addDays(cargoStartDate, periodDays - 1), 'yyyy-MM-dd');

  const queryClient = useQueryClient();

  // Queries
  const { data: calendarData, isLoading: isLoadingCalendar, error: calendarError } = useCalendarUtilization(monthFrom, monthTo);
  const { data: trailers, isLoading: isLoadingTrailers } = useTrailers();
  const { data: cargoData, isLoading: isLoadingCargo, error: cargoError } = useCargoCalendar(cargoFrom, cargoTo);

  // Cargo type colors
  const { data: cargoColors } = useQuery<CargoColorsResponse>({
    queryKey: ['cargoColors'],
    queryFn: () => api.getCargoTypeColors(),
  });

  const updateColorsMutation = useMutation({
    mutationFn: (colors: Record<string, string>) => api.updateCargoTypeColors(colors),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cargoColors'] });
    },
  });

  const resetColorsMutation = useMutation({
    mutationFn: () => api.resetCargoTypeColors(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cargoColors'] });
    },
  });

  const handleUpdateColors = async (colors: Record<string, string>) => {
    await updateColorsMutation.mutateAsync(colors);
  };

  const handleResetColors = async () => {
    await resetColorsMutation.mutateAsync();
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
  };

  const handlePreviousPeriod = () => {
    setCargoStartDate(prev => addDays(prev, -periodDays));
  };

  const handleNextPeriod = () => {
    setCargoStartDate(prev => addDays(prev, periodDays));
  };

  const handlePeriodChange = (days: number) => {
    setPeriodDays(days);
  };

  if (calendarError && tabValue === 1) {
    return (
      <Box>
        <Alert severity="error">
          Kunne ikke hente kalenderdata. Kontroller din DORA API-forbindelse.
        </Alert>
      </Box>
    );
  }

  const isLoadingUtilization = isLoadingCalendar || isLoadingTrailers;

  // Calculate statistics for utilization view
  const totalDays = calendarData?.entries?.length || 0;
  const activeDays = calendarData?.entries?.filter(e => e.isActive).length || 0;
  const uniqueTrailers = new Set(calendarData?.entries?.map(e => e.trailerId)).size;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Kalender</Typography>

        {/* Tab navigation */}
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ minHeight: 36 }}
        >
          <Tab
            icon={<ViewWeekIcon />}
            iconPosition="start"
            label="Cargo Oversigt"
            sx={{ minHeight: 40 }}
          />
          <Tab
            icon={<CalendarMonthIcon />}
            iconPosition="start"
            label="Udnyttelseskalender"
            sx={{ minHeight: 40 }}
          />
        </Tabs>
      </Box>

      {/* Cargo Calendar Tab */}
      {tabValue === 0 && (
        <>
          {/* Period navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton onClick={handlePreviousPeriod}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body1" sx={{ minWidth: 250, textAlign: 'center' }}>
              {format(cargoStartDate, 'd. MMMM', { locale: da })} - {format(addDays(cargoStartDate, periodDays - 1), 'd. MMMM yyyy', { locale: da })}
            </Typography>
            <IconButton onClick={handleNextPeriod}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <CargoCalendar
            trailers={cargoData?.trailers || []}
            teams={cargoData?.teams || []}
            isLoading={isLoadingCargo}
            error={cargoError as Error | null}
            periodDays={periodDays}
            onPeriodChange={handlePeriodChange}
            startDate={cargoStartDate}
            cargoTypeColors={cargoColors?.colors}
            onUpdateColors={handleUpdateColors}
            onResetColors={handleResetColors}
            isUpdatingColors={updateColorsMutation.isPending || resetColorsMutation.isPending}
          />
        </>
      )}

      {/* Utilization Calendar Tab */}
      {tabValue === 1 && (
        <>
          {/* Month navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 3 }}>
            <IconButton onClick={handlePreviousMonth}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center' }}>
              {format(currentMonth, 'MMMM yyyy', { locale: da })}
            </Typography>
            <IconButton onClick={handleNextMonth}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {isLoadingUtilization ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : calendarData ? (
            <>
              {/* Statistics */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <KPICard
                    title="Udnyttelsesgrad"
                    value={`${calendarData.utilizationRate.toFixed(1)}%`}
                    color={calendarData.utilizationRate >= 70 ? 'success' : calendarData.utilizationRate >= 40 ? 'warning' : 'error'}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KPICard
                    title="Aktive dage"
                    value={activeDays}
                    subtitle={`af ${totalDays} mulige`}
                    color="info"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KPICard
                    title="Køretøjer"
                    value={uniqueTrailers}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <KPICard
                    title="Tomme dage"
                    value={totalDays - activeDays}
                    color="warning"
                  />
                </Grid>
              </Grid>

              {/* Calendar */}
              <UtilizationCalendar
                data={calendarData.byDate}
                month={currentMonth}
                trailers={trailers?.map(t => ({ id: t.id, number: t.number })) || []}
              />

              {/* Trailer breakdown */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Udnyttelse per køretøj
                  </Typography>
                  <Grid container spacing={1}>
                    {trailers?.map(trailer => {
                      const trailerEntries = calendarData.entries.filter(e => e.trailerId === trailer.id);
                      const trailerActive = trailerEntries.filter(e => e.isActive).length;
                      const trailerRate = trailerEntries.length > 0
                        ? (trailerActive / trailerEntries.length) * 100
                        : 0;

                      return (
                        <Grid item xs={6} sm={4} md={3} lg={2} key={trailer.id}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              bgcolor: trailerRate >= 70 ? 'success.50' : trailerRate >= 40 ? 'warning.50' : 'grey.100',
                              border: '1px solid',
                              borderColor: trailerRate >= 70 ? 'success.200' : trailerRate >= 40 ? 'warning.200' : 'grey.300',
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {trailer.number}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {trailerRate.toFixed(0)}% ({trailerActive}/{trailerEntries.length} dage)
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </>
          ) : null}
        </>
      )}
    </Box>
  );
}
